"use client";

import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { BankValuationCompleteFormSchemaType } from "@/schemas/validation/bankValuationSchema";
import {
  ALBA_PRICE_HIGH,
  ALBA_PRICE_LOW,
  DISABLED_DATES,
  HOME_PRICE,
  HOME_TIME_SLOTS,
  INSPECTION_API_URL,
  INSPECTION_BUFFER_MS,
  VALUATION_THRESHOLD,
} from "./inspectionConstants";

function buildCalendarDays(year: number, month: number): (number | null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = Array(firstDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

const formatDateAsLocalYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const slotToUtcMs = (slot: string, dateStr: string): number => {
  const [time, period] = slot.split(" ");
  const [h, m] = time.split(":").map(Number);
  let hours = h;
  if (period === "PM" && h !== 12) hours += 12;
  if (period === "AM" && h === 12) hours = 0;
  const [year, month, day] = dateStr.split("-").map(Number);
  return Date.UTC(year, month - 1, day, hours - 4, m, 0, 0);
};

// If today is disabled, returns the next available date. Otherwise returns today.
const getFirstAvailableDate = (from: Date): Date => {
  const d = new Date(from);
  for (let i = 0; i < 30; i++) {
    if (!DISABLED_DATES.includes(formatDateAsLocalYmd(d))) return d;
    d.setDate(d.getDate() + 1);
  }
  return from;
};

type BookedInterval = { startMs: number; endMs: number };

export type InspectionType = "alba" | "home";

const INSPECTION_TIME_SLOTS = HOME_TIME_SLOTS;
const SLOT_DURATION_MS = 90 * 60 * 1000;

export function useInspectionForm() {
  const { watch, setValue } = useFormContext<BankValuationCompleteFormSchemaType>();

  const dealDrivePrice = watch("dealDriveEstimatedPrice") as number | undefined;
  const manualPrice = watch("value") as number | string | undefined;

  const parsedManualPrice = manualPrice ? Number(String(manualPrice).replace(/,/g, "")) : undefined;

  const marketPrice =
    dealDrivePrice && dealDrivePrice > 0
      ? dealDrivePrice
      : parsedManualPrice && !isNaN(parsedManualPrice) && parsedManualPrice > 0
        ? parsedManualPrice
        : undefined;

  const specs = watch("specs") as string | undefined;

  // ── Pricing ────────────────────────────────────────────────────────────────
  const isGcc = (specs ?? "").toLowerCase().trim().includes("gcc");
  const isHighTier = (marketPrice != null && marketPrice > VALUATION_THRESHOLD) || !isGcc;
  const albaPriceAed = isHighTier ? ALBA_PRICE_HIGH : ALBA_PRICE_LOW;
  const homePriceAed = HOME_PRICE;

  // ── Hide home inspection for GCC cars under 250k ───────────────────────────
  const showHomeInspection = isHighTier;

  // ── Inspection type ────────────────────────────────────────────────────────
  const [inspectionType, setInspectionType] = useState<InspectionType>("alba");
  const inspectionPrice = inspectionType === "alba" ? albaPriceAed : homePriceAed;

  useEffect(() => {
    if (!showHomeInspection && inspectionType === "home") {
      setInspectionType("alba");
      setValue("inspectionType" as any, "alba");
      setValue("inspectionPrice" as any, albaPriceAed);
    }
  }, [showHomeInspection, inspectionType, albaPriceAed, setValue]);

  useEffect(() => {
    setValue("inspectionType" as any, "alba");
    setValue("inspectionPrice" as any, albaPriceAed);
  }, [albaPriceAed, setValue]);

  // ── Calendar ───────────────────────────────────────────────────────────────
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() + 30);
    return d;
  }, [today]);

  // If today is disabled, auto-select the next available date instead
  const firstAvailableDate = useMemo(() => getFirstAvailableDate(today), [today]);

  const [calYear, setCalYear] = useState(firstAvailableDate.getFullYear());
  const [calMonth, setCalMonth] = useState(firstAvailableDate.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(firstAvailableDate);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const calendarDays = useMemo(() => buildCalendarDays(calYear, calMonth), [calYear, calMonth]);

  const isDateDisabled = (day: number) => {
    const d = new Date(calYear, calMonth, day);
    const dateStr = formatDateAsLocalYmd(d);
    return d < today || d > maxDate || DISABLED_DATES.includes(dateStr);
  };

  const isPrevMonthDisabled = () => calYear === today.getFullYear() && calMonth === today.getMonth();

  const isNextMonthDisabled = () => calYear === maxDate.getFullYear() && calMonth === maxDate.getMonth();

  const prevMonth = () => {
    if (isPrevMonthDisabled()) return;
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else setCalMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (isNextMonthDisabled()) return;
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else setCalMonth((m) => m + 1);
  };

  const isSelectedDate = (day: number) =>
    !!selectedDate &&
    selectedDate.getFullYear() === calYear &&
    selectedDate.getMonth() === calMonth &&
    selectedDate.getDate() === day;

  const isToday = (day: number) =>
    today.getFullYear() === calYear && today.getMonth() === calMonth && today.getDate() === day;

  const isSelectedToday =
    !!selectedDate &&
    selectedDate.getFullYear() === today.getFullYear() &&
    selectedDate.getMonth() === today.getMonth() &&
    selectedDate.getDate() === today.getDate();

  // ── Booked intervals from inspection app API ───────────────────────────────
  const [bookedIntervals, setBookedIntervals] = useState<BookedInterval[]>([]);
  const [isFetchingSlots, setIsFetchingSlots] = useState(false);
  const [selectedDateStr, setSelectedDateStr] = useState<string>(formatDateAsLocalYmd(firstAvailableDate));

  const fetchBookedSlots = async (date: Date) => {
    const dateStr = formatDateAsLocalYmd(date);
    setSelectedDateStr(dateStr);
    setIsFetchingSlots(true);
    try {
      const res = await fetch(`${INSPECTION_API_URL}/getBookedSlots?date=${dateStr}`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();

      const intervals: BookedInterval[] = (data.booked_slots ?? []).map(
        (slot: { start_time: string; end_time: string }) => ({
          startMs: new Date(slot.start_time).getTime(),
          endMs: new Date(slot.end_time).getTime(),
        }),
      );

      setBookedIntervals(intervals);
    } catch {
      setBookedIntervals([]);
    } finally {
      setIsFetchingSlots(false);
    }
  };

  // ── Inspection type change ─────────────────────────────────────────────────
  const handleInspectionTypeChange = (type: InspectionType) => {
    setInspectionType(type);
    setSelectedDate(firstAvailableDate);
    setSelectedTime(null);
    setBookedIntervals([]);
    setValue("inspectionType" as any, type);
    setValue("inspectionPrice" as any, type === "alba" ? albaPriceAed : homePriceAed);
    setValue("inspectionDate" as any, formatDateAsLocalYmd(firstAvailableDate));
    setValue("inspectionTime" as any, undefined);
    fetchBookedSlots(firstAvailableDate);
  };

  // ── Date select ────────────────────────────────────────────────────────────
  const handleDateSelect = (day: number) => {
    if (isDateDisabled(day)) return;
    const date = new Date(calYear, calMonth, day);
    setSelectedDate(date);
    setSelectedTime(null);
    setValue("inspectionDate" as any, formatDateAsLocalYmd(date));
    fetchBookedSlots(date);
  };

  // Keep selected date synced in form and fetch slots on initial render
  useEffect(() => {
    if (!selectedDate) return;
    setValue("inspectionDate" as any, formatDateAsLocalYmd(selectedDate));
    fetchBookedSlots(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Time slots — filter by overlap with booked intervals + past slots ───────
  const availableTimeSlots = useMemo(() => {
    const slotOverlapsBooking = (slotUtcMs: number): boolean => {
      const slotEndUtcMs = slotUtcMs + SLOT_DURATION_MS;
      return bookedIntervals.some(({ startMs, endMs }) => slotUtcMs < endMs && slotEndUtcMs > startMs);
    };

    const cutoff = Date.now() + INSPECTION_BUFFER_MS;

    return INSPECTION_TIME_SLOTS.filter((slot) => {
      const slotUtcMs = slotToUtcMs(slot, selectedDateStr);

      if (slotOverlapsBooking(slotUtcMs)) return false;

      if (isSelectedToday && slotUtcMs <= cutoff) return false;

      return true;
    });
  }, [bookedIntervals, isSelectedToday, selectedDateStr]);

  // ── Time select ────────────────────────────────────────────────────────────
  const handleTimeSelect = (slot: string) => {
    if (!availableTimeSlots.includes(slot)) return;
    setSelectedTime(slot);
    setValue("inspectionTime" as any, slot);
  };

  // ── Location ───────────────────────────────────────────────────────────────
  const [locationAddress, setLocationAddress] = useState("");

  const handleLocationChange = (address: string, lat: number, lng: number) => {
    setLocationAddress(address);
    setValue("inspectionLocation" as any, address || undefined);
    setValue("inspectionLatitude" as any, lat);
    setValue("inspectionLongitude" as any, lng);
  };

  return {
    // pricing
    albaPriceAed,
    homePriceAed,
    inspectionType,
    inspectionPrice,
    showHomeInspection,
    handleInspectionTypeChange,
    // calendar
    today,
    calYear,
    calMonth,
    calendarDays,
    selectedDate,
    isDateDisabled,
    isPrevMonthDisabled,
    isNextMonthDisabled,
    prevMonth,
    nextMonth,
    handleDateSelect,
    isSelectedDate,
    isToday,
    // time
    availableTimeSlots,
    isFetchingSlots,
    selectedTime,
    handleTimeSelect,
    // location
    locationAddress,
    handleLocationChange,
  };
}
